'use client';

import {
  AnalyticsContext,
  AnalyticsProvider as CarvanaAnalyticsProvider
} from '@carvana/eventable-clickstream';
import trackPerformance from '@carvana/eventable-web-vitals';
import { useEffectOnce } from '@carvana/merch-react-hooks';
import React, { useContext, useMemo } from 'react';
import { commonEnvironment, environment } from '~/constants/environment';
import {
  consumeSrpTileClickElapsed,
  getLoadNavType,
  isSpaNavigation,
  navPerformanceNow
} from '~/utilities/navigationTiming';

type AnalyticsProviderProps = React.ComponentProps<
  typeof CarvanaAnalyticsProvider
>;

type OwnProps = React.PropsWithChildren<
  Partial<AnalyticsProviderProps> &
    Pick<AnalyticsProviderProps, 'projectName' | 'track'>
>;

type TrackRenderedProps = {
  payloadMetaAttributes: object;
  projectName: string;
  track: string;
};

function TrackRendered({
  payloadMetaAttributes,
  projectName,
  track
}: TrackRenderedProps) {
  const { trackEvent } = useContext(AnalyticsContext);

  useEffectOnce(() => {
    trackPerformance({
      environment: commonEnvironment,
      payloadMetaAttributes,
      project: projectName,
      track
    });
  }, [payloadMetaAttributes, projectName, track]);

  useEffectOnce(() => {
    // New generic load time for when we have started ui-render
    trackEvent('load-time', {
      navMode: isSpaNavigation() ? 'spa' : 'doc',
      navType: getLoadNavType(),
      unit: 'ms',
      value: navPerformanceNow()
    });
  }, [trackEvent]);

  useEffectOnce(() => {
    // Audit event scoped to SRP tile click → VDP navigations, fired for both
    // SPA (treatment) and document (control) arrivals so they can be compared
    const loadTime = consumeSrpTileClickElapsed();
    if (loadTime !== undefined) {
      trackEvent('srp-tile-click-time', {
        loadTime,
        navMode: isSpaNavigation() ? 'spa' : 'doc'
      });
    }
  }, [trackEvent]);

  useEffectOnce(() => {
    // Do not add anything else to this event, on threat of strong disapproval and dirty looks.
    trackEvent('ui-rendered', undefined, undefined, {
      useFetch: true,
      fetchOption: {
        keepalive: false
      }
    });
  }, [trackEvent]);

  return null;
}

export function LayoutAnalyticsProvider({
  children,
  projectName,
  track,
  ...rest
}: OwnProps) {
  const payloadMetaAttributes = useMemo(
    () => ({
      appVersion: process.env.APP_VERSION,
      ...rest.payloadMetaAttributes
    }),
    [rest.payloadMetaAttributes]
  );

  return (
    <CarvanaAnalyticsProvider
      useReferrerOverride
      {...rest}
      environment={environment}
      payloadMetaAttributes={payloadMetaAttributes}
      projectName={projectName}
      track={track}
    >
      {children}
    </CarvanaAnalyticsProvider>
  );
}

export default function AnalyticsProvider({
  children,
  projectName,
  track,
  ...rest
}: OwnProps) {
  const payloadMetaAttributes = useMemo(
    () => ({
      appVersion: process.env.APP_VERSION,
      ...rest.payloadMetaAttributes
    }),
    [rest.payloadMetaAttributes]
  );

  return (
    <CarvanaAnalyticsProvider
      useReferrerOverride
      {...rest}
      environment={environment}
      payloadMetaAttributes={payloadMetaAttributes}
      projectName={projectName}
      track={track}
    >
      <TrackRendered
        payloadMetaAttributes={payloadMetaAttributes}
        projectName={projectName}
        track={track}
      />
      {children}
    </CarvanaAnalyticsProvider>
  );
}

export function useTrackEvent() {
  return useContext(AnalyticsContext).trackEvent;
}
